'use client'

import CloseButton from '@components/CloseButton'
import { Dialog, DialogHeader, LoadingDialog } from '@components/DewiDialog'
import { Box } from '@mui/material'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useAuctionManager from '@hooks/useAuctionManager'
import BidItem from './BidItem'
import { useMetaplexMetadata } from '@hooks/useMetaplexMetadata'
import { DewiCard } from '@containers/auction'
import Image from 'next/image'

type BidsDialogProps = {
  currentDewiCat: DewiCard
  open: boolean
  onClose: () => void
}

const BidsDialog = ({ currentDewiCat, open, onClose }: BidsDialogProps) => {
  const { image } = useAuctionManager()
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const onCloseHeader = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          padding: '0',
        },
      }}
    >
      <>
        <DialogHeader
          sx={{
            padding: '1.375rem',
          }}
        >
          <Box alignSelf="flex-end">
            <CloseButton onClick={onCloseHeader} />
          </Box>
        </DialogHeader>
        <div className="flex flex-col p-4 pt-0 gap-4 lg:w-[550px] overflow-y-auto">
          <div className="flex flex-row gap-2">
            <Image
              src={image}
              alt="DewiCat"
              width={100}
              height={100}
              className="rounded-lg"
            />
            <div className="flex flex-col">
              <h4 className="text-size-3 text-gray-6 mt-2">Bids for</h4>
              <h4 className="text-size-6">{currentDewiCat.name}</h4>
            </div>
          </div>
          <div className="rounded-lg overflow-y-auto">
            <BidList tokenMintDecimals={currentDewiCat.decimals} />
          </div>
        </div>
      </>
    </Dialog>
  )
}

export const BidList = ({
  tokenMintDecimals,
}: {
  tokenMintDecimals: number
}) => {
  const { t } = useTranslation()
  const { bids, currentListing } = useAuctionManager()
  const { image } = useMetaplexMetadata(currentListing?.tokenMint)

  return (
    <Box
      className="flex flex-col gap-2"
      sx={{
        overflow: 'overlay',
      }}
    >
      {!bids && <LoadingDialog title="Loading" />}

      {bids
        ?.sort((a, b) => b.amount.toNumber() - a.amount.toNumber())
        .map((bid) => (
          <BidItem
            key={bid.bidder.toBase58()}
            bid={bid}
            image={image}
            tokenMintDecimals={tokenMintDecimals}
          />
        ))}
    </Box>
  )
}

export default BidsDialog
